import React, { useState } from "react";
import {
  Document,
  Page,
  StyleSheet,
  Text,
  View,
  Image,
  Font,
  Link,
  Svg,
} from "@react-pdf/renderer";
import {
  convertDecimalDosage,
  getClinicEmail,
  getDoctorsRegistrationNumber,
  isAkerkerAartiCrossoverClinic,
} from "./utils";

import archivoBlack from "../../fonts/ArchivoBlack-Regular.ttf";
import poppinsItalic from "../../fonts/Poppins-MediumItalic.ttf";
import devnagriFont from "../../fonts/NotoSerifDevanagari-Regular.ttf";
import devnagriFontBold from "../../fonts/NotoSerifDevanagari-Bold.ttf";

import ic4Logo from "../../img/ic4-logo.svg";
import DrAartiThaneLogo from "../../img/dr_aarti_thane_logo.jpeg";
import GoodHealthClinicLogo from "../../img/good-health-superspeciality-clinics-logo.png";
import SpandanClinicLogo from "../../img/spandanClinicLogo.jpg";
import {
  getTranslatedDosageHeader,
  getTranslatedDuration,
  getTranslatedInstructions,
  getTranslatedPrescribedBySentence,
  getTranslatedPrescriptionDoctorName,
  getTranslatedPrescriptionHeaderText,
  getTranslatedPrescriptionNumbers,
  getTranslatedPrescriptionTitleText,
  isDevanagariScriptLanguage,
  isTextInDevanagari,
} from "./translations";

const primaryColor = "#f6781b";
const secondaryColor = "#0e426a";

const dummyMedicines = [
  {
    name: "Tablet Atrova",
    nameLabel: "20 days Tablet(s)",
    strength: "10 mg",
    frequency: [0, 0, 1],
    instructions: "",
  },
  {
    name: "Tablet Foltirax",
    nameLabel: "3 days Tablet(s)",
    strength: "2.5 mg",
    frequency: [0, 1, 0],
    instructions: "Monday",
  },
  {
    name: "Capsule Uprise",
    nameLabel: "1 days Tablet(s)",
    strength: "600000 IU",
    frequency: [1, 0, 0],
    instructions: "Once a month",
  },
];

export const dummyData = {
  pFullName: "Santosh Kumar",
  pGender: "Male",
  pAge: "58 yrs.",
  pId: "21-000-080",
  diagnosis: "Ankylosing Spondylitis",
  consultDate: "05 Feb 2023",
  consultType: "In person",
  medicines: dummyMedicines,
  splInstructions: [
    "For any emergency, contact casualty at Fortis Hospital Mulund (022 43654364), Bethany Hospital Thane (022 69115100), Godrej Hospital Vikhroli (022 66417100)",
    "For outstation, do consult your physician & keep us updated.",
    "Generic medicines may be taken from govt authorized shops.",
  ],
  splAdvice: [
    "Do not continue medicines beyond the prescribed date. They are potentially harmful without supervision.",
    "Do not plan pregnancy without informing in advance.",
    "In case of any unusual symptom with any medication; contact me / family physician immediately",
  ],
  followUpDate: "05 Mar 2023",
  pathologyTests: "CBC ESR, CRP, SGOT SGPT, Creat",
  bodyScanTests:
    "Thoracic spine, Knee bil AP standing, SI joint prone, Bilateral hands with wrist, Cervical spine- AP/lat",
};

Font.register({
  family: "Lato",
  src: `https://staging.ic4.health/patient/static/Rubik-Regular.ttf`,
});

Font.register({
  family: "Lato Italic",
  src: `https://staging.ic4.health/patient/static/Rubik-Italic.ttf`,
});

Font.register({
  family: "Lato Bold",
  src: `https://staging.ic4.health/patient/static/Rubik-Bold.ttf`,
});

Font.register({
  family: "ArchivoBlack Regular",
  src: archivoBlack,
});
Font.register({
  family: "Devnagri",
  src: devnagriFont,
});
Font.register({
  family: "Devnagri Bold",
  src: devnagriFontBold,
});
Font.register({
  family: "Poppins Italic",
  src: poppinsItalic,
});
Font.registerHyphenationCallback((word) => {
  // Return entire word as unique part
  return [word];
});

export default function PxPDFView({ pxInfo, profile, clinic, lang }) {
  const [px, setPx] = useState(pxInfo);
  const clinicDetails = clinic;

  const styles = StyleSheet.create({
    page: {
      backgroundColor: "#fff",
      paddingTop: 25,
      paddingBottom: 25,
      paddingRight: 25,
      paddingLeft: 25,
    },

    logo: {
      width: "100px",
    },
    // footer: {
    //   textAlign: "right",
    //   width: "100%",
    //   fontFamily: "Lato",
    //   fontSize: "12px",
    //   borderTop: "1px",
    //   borderColor: "#D3D3D3",
    // },
    signatureContainer: {
      borderColor: "#D3D3D3",
      flexDirection: "row",
      flexWrap: "wrap",
      width: "100%",
      marginTop: "20px",
    },
    signatureDrName: {
      fontFamily: isDevanagariScriptLanguage(lang)
        ? "Devnagri Bold"
        : "Lato Bold",
      fontSize: "15px",

      marginBottom: "1px",
      wordBreak: "break-word",
      hyphens: "none",
    },
    signatureMultiDrName: {
      fontFamily: isDevanagariScriptLanguage(lang)
        ? "Devnagri Bold"
        : "Lato Bold",
      fontSize: "12px",
      textAlign: "center",

      marginBottom: "1px",
      wordBreak: "break-word",
      hyphens: "none",
      width: "100%",
    },
    container: {
      flexDirection: "row",
      flexWrap: "wrap",
      width: "100%",

      // paddingTop: profile.code === "DR000010" ? "110px" : "0px",
    },
    break: {
      flexBasis: "100%",
      flexDirection: "row",
      height: 0,
      backgroundColor: "yellow",
    },
    leftColumn: {
      width: "68%",
      paddingRight: 15,
      //backgroundColor: "pink"
    },
    rightColumn: {
      flexGrow: 1,
      width: "32%",

      //backgroundColor: "gray"
    },
    oneColumn: {
      width: "100%",

      borderBottom: "1px",
      borderColor: "#D3D3D3",
      marginBottom: "10px",
    },
    blueDivider: {
      width: "100%",
      borderBottom: "3px",
      borderColor: secondaryColor,
      marginBottom: "10px",
    },
    footerColumn: {
      width: "100%",
      marginTop: "20px",
      borderBottom: "1px",
      borderColor: "#D3D3D3",
      marginBottom: "10px",
    },

    heading1: {
      fontFamily: "Lato Bold",
      fontSize: "15px",
      // color: primaryColor,
      marginBottom: "5px",
      wordBreak: "break-word",
      hyphens: "none",
      width: "200%",
    },
    heading2: {
      fontFamily: "Lato Bold",
      fontSize: "15px",
      marginBottom: "5px",
      marginTop: "15px",
      color: secondaryColor,
    },
    heading3: {
      fontFamily: "Lato",
      fontSize: "8px",
      marginBottom: "5px",
      maxWidth: "245px",
    },
    smallText: {
      fontFamily: isDevanagariScriptLanguage(lang) ? "Devnagri" : "Lato",
      fontSize: "10px",
      marginBottom: "5px",
    },

    labelText: {
      fontFamily: isDevanagariScriptLanguage(lang)
        ? "Devnagri Bold"
        : "Lato Bold",
      color: "black",
      fontSize: "11px",
      fontWeight: "bold",
      marginBottom: "1px",
    },
    valueText: {
      fontFamily: "Lato",
      fontSize: "11px",
    },
    nameText: {
      fontFamily: isTextInDevanagari(px.pFullName) ? "Devnagri" : "Lato",
      fontSize: "11px",
    },
    tableHeader: {
      fontFamily: isDevanagariScriptLanguage(lang)
        ? "Devnagri Bold"
        : "Lato Bold",

      fontSize: "10px",
      paddingTop: "5px",
      paddingBottom: "5px",
      backgroundColor: "#D3D3D3",
      textAlign: "center",
    },
    tableRow: {
      fontFamily: "Lato",
      fontSize: "11px",
    },
    tableRowLabel: {
      fontFamily: isDevanagariScriptLanguage(lang) ? "Devnagri" : "Lato",
      fontSize: "9px",
      paddingBottom: "5px",
      color: "black",
    },
    freqLabel: {
      fontFamily: isDevanagariScriptLanguage(lang) ? "Devnagri" : "Lato",
      fontSize: "9px",
      paddingBottom: "5px",
      color: "black",
      margin: "auto",
    },
    footer: {
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      margin: 25,
      marginBottom: 10,
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      borderTop: "3px",
      borderColor: secondaryColor,
      paddingTop: 1,
    },
    pageNumber: {
      fontSize: 10,
      textAlign: "left",
      color: "black",
      width: "82%",
    },
    linkText: {
      fontSize: 10,
      // textAlign: "right",
      color: "black",
      marginRight: "18px",
      textDecoration: "none",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "end",
      // width: "10%",
    },
    doctorCredentials: {
      fontFamily: "Lato",
      fontSize: "8px",
      marginBottom: "5px",
      maxWidth: "148px",
      whiteSpace: "nowrap",
      wordWrap: "normal",
    },
  });

  const MedicinePrescriptionView = (medicines) =>
    medicines.map((medicine, index) => {
      return (
        <View
          key={index}
          style={{
            flexDirection: "row",
            minHeight: 30,
            width: "100%",
            marginBottom: 2,
          }}
          wrap={false}
        >
          {/* Index (fixed) */}
          <View style={{ width: "5%" }}>
            <Text
              style={{
                fontFamily: isDevanagariScriptLanguage(lang)
                  ? "Devnagri"
                  : "Lato",
                fontSize: 11,
              }}
            >
              {"  "}
              {getTranslatedPrescriptionNumbers(index + 1, lang)}
            </Text>
          </View>

          {/* LEFT PANE: fills space between index and instructions */}
          <View
            style={{
              flexGrow: 1,
              flexShrink: 1,
              minWidth: 0,
              width: "55%",
              flexDirection: "column",
            }}
          >
            {/* Line 1: name (flex) + dosage (fixed 130) */}
            <View style={{ flexDirection: "row" }}>
              <View
                style={{
                  flexGrow: 1,
                  flexShrink: 1,
                  minWidth: 0,
                }}
              >
                <Text style={styles.tableRow}>
                  {medicine.name}
                  {medicine.strength && ` (${medicine.strength})`}
                </Text>
              </View>

              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "center",
                  textAlign: "center",
                  width: 130,
                }}
              >
                <Text
                  style={{
                    width: 30,
                    fontSize: 11,
                    fontFamily: isDevanagariScriptLanguage(lang)
                      ? "Devnagri"
                      : "Lato",
                  }}
                >
                  {getTranslatedPrescriptionNumbers(
                    medicine.frequency[0],
                    lang
                  )}
                </Text>
                <Text style={{ width: 30, fontFamily: "Lato", fontSize: 11 }}>
                  {"  "}-{"   "}
                </Text>
                <Text
                  style={{
                    width: 30,
                    fontSize: 11,
                    fontFamily: isDevanagariScriptLanguage(lang)
                      ? "Devnagri"
                      : "Lato",
                  }}
                >
                  {getTranslatedPrescriptionNumbers(
                    medicine.frequency[1],
                    lang
                  )}
                </Text>
                <Text style={{ width: 30, fontFamily: "Lato", fontSize: 11 }}>
                  {"  "}-{"   "}
                </Text>
                <Text
                  style={{
                    width: 30,
                    fontSize: 11,
                    fontFamily: isDevanagariScriptLanguage(lang)
                      ? "Devnagri"
                      : "Lato",
                  }}
                >
                  {getTranslatedPrescriptionNumbers(
                    medicine.frequency[3],
                    lang
                  )}
                </Text>
                <Text
                  style={{
                    width: 30,
                    fontFamily: isDevanagariScriptLanguage(lang)
                      ? "Devnagri"
                      : "Lato Bold",
                    fontSize: 11,
                  }}
                >
                  {"  "}-{"   "}
                </Text>
                <Text
                  style={{
                    width: 30,
                    fontSize: 11,
                    fontFamily: isDevanagariScriptLanguage(lang)
                      ? "Devnagri"
                      : "Lato",
                  }}
                >
                  {getTranslatedPrescriptionNumbers(
                    medicine.frequency[2],
                    lang
                  )}
                </Text>
              </View>
            </View>

            {/* Line 2: composition — inside LEFT pane, so it can't clash with instructions */}
            <View>
              <Text
                style={{
                  fontFamily: "Lato",
                  fontSize: 9,
                  color: "#666666",
                  marginTop: 0,
                }}
              >
                {medicine?.composition}
              </Text>
            </View>
          </View>

          {/* RIGHT PANE: fixed width in points so left pane can’t flow under it */}
          <View style={{ width: "38%", textAlign: "left", paddingLeft: 8 }}>
            {medicine.instructions?.trim().length > 0 && (
              <Text
                style={{
                  fontFamily: isDevanagariScriptLanguage(lang)
                    ? "Devnagri"
                    : "Lato",
                  fontSize: 11,
                }}
              >
                {getTranslatedInstructions(medicine.instructions, lang)}
              </Text>
            )}
            <Text style={styles.tableRowLabel}>
              {medicine.nameLabel &&
                getTranslatedDuration(medicine.nameLabel, lang)}
            </Text>
          </View>
        </View>
      );
    });

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.container}>
          {/*Header = Logo + Credentials - FIXED*/}
          {
            <>
              <View style={styles.leftColumn} fixed>
                {clinic?.clinicId === "CC000006" ? (
                  <Image
                    style={{
                      maxHeight: "50px",
                      objectFit: "contain",
                      width: "auto",
                      marginRight: "auto",
                      marginBottom: "5px",
                    }}
                    src={DrAartiThaneLogo}
                  />
                ) : clinic?.clinicId === "CC000009" ? (
                  <Image
                    style={{
                      maxHeight: "50px",
                      objectFit: "contain",
                      width: "auto",
                      marginRight: "auto",
                      marginBottom: "5px",
                    }}
                    src={SpandanClinicLogo}
                  />
                ) : clinic?.clinicId === "CC000012" ||
                  clinic?.clinicId === "CC000013" ? (
                  <Image
                    style={{
                      maxHeight: "60px",
                      objectFit: "contain",
                      width: "auto",
                      marginRight: "auto",
                      marginBottom: "5px",
                    }}
                    src={GoodHealthClinicLogo}
                  />
                ) : (
                  <Image
                    src="https://staging.ic4.health/patient/ic4-logo.png"
                    style={styles.logo}
                  />
                )}
                {!(
                  clinic?.clinicId === "CC000003" ||
                  clinic?.clinicId === "CC000009" ||
                  clinic?.clinicId === "CC000006" ||
                  clinic?.clinicId === "CC000012" ||
                  clinic?.clinicId === "CC000013"
                ) && (
                  <View style={styles.heading2}>
                    <Text>
                      {/* Note: Some entity name contains ic4.health, in this case we avoid printing ic4  */}
                      {clinicDetails?.address?.entityName
                        ? clinicDetails.address.entityName.includes(",")
                          ? clinicDetails.address.entityName
                              .split(",")[1]
                              ?.trim()
                          : clinicDetails.address.entityName
                        : ""}
                    </Text>
                  </View>
                )}
              </View>
              <View style={styles.rightColumn} fixed>
                <Text style={styles.heading1}>{px?.doctorDetails?.name}</Text>
                <View style={styles.doctorCredentials}>
                  <Text>{px?.doctorDetails?.credentials}</Text>
                  {/* <Text>{profile.credentials.split(",")[1].substring(1)}</Text> */}
                </View>
              </View>
              <View style={styles.blueDivider}></View>
            </>
          }

          {/*Patient Details - Row 1*/}
          <View
            style={{ width: "100%", display: "flex", flexDirection: "row" }}
          >
            <View style={{ width: "95px" }}>
              <Text style={styles.labelText}>
                {" "}
                {getTranslatedPrescriptionTitleText("Patient", lang)}:
              </Text>
            </View>
            <View style={{ width: "calc(100% - 95px)" }}>
              <Text style={styles.valueText}>
                {px.pFullName && px.pGender && px.pAge ? (
                  <>
                    <Text style={styles.nameText}>{px.pFullName}</Text> |{" "}
                    {px.pGender} | {px.pAge}
                  </>
                ) : px.pFullName && px.pGender ? (
                  <>
                    <Text style={styles.nameText}>{px.pFullName}</Text> |{" "}
                    {px.pGender}
                  </>
                ) : px.pFullName && px.pAge ? (
                  <>
                    <Text style={styles.nameText}>{px.pFullName}</Text> |{" "}
                    {px.pAge}
                  </>
                ) : (
                  px.pFullName && (
                    <Text style={styles.nameText}>{px.pFullName}</Text>
                  )
                )}
              </Text>
            </View>
          </View>

          {/*Patient Details - Row 2*/}
          <View
            style={{ width: "100%", display: "flex", flexDirection: "row" }}
          >
            <View style={{ width: "95px" }}>
              <Text style={styles.labelText}>
                {" "}
                {getTranslatedPrescriptionTitleText("PRN", lang)}:
              </Text>
            </View>
            <View style={{ width: "calc(100% - 95px)" }}>
              <Text style={styles.valueText}>{px.pId}</Text>
            </View>
          </View>
          <View style={{ marginTop: "10px", width: "100%" }}></View>
          <View
            style={{ width: "100%", display: "flex", flexDirection: "row" }}
          >
            <View style={{ width: "80px" }}>
              <Text style={styles.labelText}>
                {" "}
                {getTranslatedPrescriptionTitleText("Consult Date", lang)}:
              </Text>
            </View>
            <View style={{ width: "80px" }}>
              <Text style={styles.valueText}>{px.consultDate}</Text>
            </View>
            <View style={{ width: "80x" }}>
              <Text style={styles.labelText}>
                {" "}
                {getTranslatedPrescriptionTitleText("Consult Type", lang)}:
              </Text>
            </View>

            <View>
              <Text style={styles.valueText}>{px.consultationType}</Text>
            </View>
          </View>

          {!clinicDetails?.prescriptionConfig?.hideSymptomAndFindingNote && (
            <>
              {" "}
              <View style={{ marginTop: "10px", width: "100%" }}></View>
              {/*Patient Details - Row 3*/}
              {!clinicDetails?.prescriptionConfig?.hideSymptom && (
                <View
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <View style={{ width: "95px" }}>
                    <Text style={styles.labelText}>
                      {" "}
                      {getTranslatedPrescriptionTitleText("Complaints", lang)}:
                    </Text>
                  </View>
                  <View style={{ width: "calc(100% - 95px)" }}>
                    <Text style={styles.valueText}>
                      {px.patientComplaints ? px.patientComplaints : "NA"}
                    </Text>
                  </View>
                </View>
              )}
              <View
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "5px",
                }}
              >
                <View style={{ width: "95px" }}>
                  <Text style={styles.labelText}>
                    {" "}
                    {getTranslatedPrescriptionTitleText("Notes", lang)}:
                  </Text>
                </View>
                <View style={{ width: "calc(100% - 95px)" }}>
                  <Text style={styles.valueText}>
                    {px.todaysNotes ? px.todaysNotes : "NA"}
                  </Text>
                </View>
              </View>
            </>
          )}

          <View
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              marginTop: "10px",
            }}
          >
            <View style={{ width: "95px" }}>
              <Text style={styles.labelText}>
                {" "}
                {getTranslatedPrescriptionTitleText("Diagnosis", lang)}:
              </Text>
            </View>
            <View style={{ width: "calc(100% - 95px)" }}>
              <Text style={styles.valueText}>
                {px.diagnosis ? px.diagnosis : "NA"}
              </Text>
            </View>
          </View>

          {px?.examinationFindings && (
            <View
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                marginTop: "10px",
              }}
            >
              <View style={{ width: "95px" }}>
                <Text style={styles.labelText}>
                  {getTranslatedPrescriptionTitleText("Findings", lang)}:
                </Text>
              </View>
              <View style={{ width: "calc(100% - 95px)" }}>
                <Text style={styles.valueText}>{px.examinationFindings}</Text>
              </View>
            </View>
          )}

          {px?.procedures && (
            <View
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                marginTop: "10px",
              }}
            >
              <View style={{ width: "95px" }}>
                <Text style={styles.labelText}>
                  {getTranslatedPrescriptionTitleText("Procedures", lang)}:
                </Text>
              </View>
              <View style={{ width: "calc(100% - 95px)" }}>
                <Text style={styles.valueText}>{px.procedures}</Text>
              </View>
            </View>
          )}

          {/*Medication Prescribed*/}

          {px.medicines && px.medicines.length > 0 && (
            <>
              <View style={{ marginTop: "10px", width: "100%" }}></View>
              {/*Table Header*/}
              <View style={{ width: "5%" }}>
                <Text style={styles.tableHeader}>&nbsp;</Text>
              </View>
              <View style={{ width: "32%" }}>
                <Text style={styles.tableHeader}>
                  {getTranslatedPrescriptionHeaderText("Drug Name", lang) + " "}
                </Text>
              </View>
              {/* <View style={{ width: "15%" }}>
                <Text style={styles.tableHeader}>
                  {getTranslatedPrescriptionHeaderText("Strength", lang)}
                </Text>
              </View> */}
              <View style={{ width: "23%" }}>
                <Text style={styles.tableHeader}>
                  {"   "}
                  {getTranslatedPrescriptionHeaderText("Dosage", lang)}
                </Text>
              </View>
              <View style={{ width: "38%", textAlign: "center" }}>
                <Text style={styles.tableHeader}>
                  {getTranslatedPrescriptionHeaderText("Instructions", lang)}
                </Text>
              </View>
              {/*Frequency labels*/}
              <View
                style={{ flexDirection: "row", display: "flex", width: "100%" }}
              >
                <View
                  style={{
                    paddingTop: 10,
                    width: "160px",
                    margin: "auto",
                    // paddingLeft: "28px",

                    display: "flex",
                    justifyContent: "center",
                    transform: "translateX(-4%)",
                    whiteSpace: "nowrap",
                    overflow: "visible",
                  }}
                >
                  <Text style={styles.freqLabel}>
                    {`${getTranslatedDosageHeader(
                      "Morning",
                      lang
                    )} - ${getTranslatedDosageHeader(
                      "Afternoon",
                      lang
                    )} - ${getTranslatedDosageHeader(
                      "Evening",
                      lang
                    )} - ${getTranslatedDosageHeader("Night", lang)}`}
                  </Text>
                </View>
              </View>

              {/*Table Rows*/}
              {MedicinePrescriptionView(px.medicines)}
            </>
          )}

          <View
            style={{
              width: "100%",
              borderTop: "1px",
              borderColor: "#D3D3D3",
              marginBottom: "10px",
            }}
          ></View>
          {/*FOLLOW UP*/}
          {px?.followUpDate && (
            <View style={{ width: "100%", marginTop: "5px" }} wrap={false}>
              <Text>
                <Text style={styles.labelText}>
                  {getTranslatedPrescriptionTitleText(
                    "Recommended Follow Up",
                    lang
                  )}
                  :{" "}
                </Text>
                <Text style={styles.valueText}>{px.followUpDate}</Text>
              </Text>
            </View>
          )}

          {/*TESTS*/}
          {console.log(px, "prescPg")}
          {(px?.pathologyTests?.length > 0 ||
            px?.bodyScanTests?.length > 0) && (
            <>
              <View style={{ marginTop: "5px", width: "100%" }}></View>
              <View style={{ width: "100%" }} wrap={true}>
                {px.pathologyTests && px.pathologyTests.length > 0 && (
                  <View style={{ width: "100%", marginTop: "5px" }}>
                    <Text>
                      <Text style={styles.labelText}>
                        {getTranslatedPrescriptionTitleText(
                          "Advised Pathology Tests",
                          lang
                        )}
                        :{" "}
                      </Text>
                      <Text style={styles.valueText}>{px.pathologyTests}</Text>
                    </Text>
                  </View>
                )}

                {px.bodyScanTests && px.bodyScanTests.length > 0 && (
                  <View style={{ width: "100%", marginTop: "5px" }}>
                    <Text>
                      <Text style={styles.labelText}>
                        {getTranslatedPrescriptionTitleText(
                          "Advised Radiology Tests",
                          lang
                        )}
                        :{" "}
                      </Text>
                      <Text style={styles.valueText}>{px.bodyScanTests}</Text>
                    </Text>
                  </View>
                )}
              </View>
            </>
          )}

          {(px?.splInstructions?.length > 0 || px?.splAdvice?.length > 0) && (
            <>
              <View style={{ marginTop: "5px", width: "100%" }}></View>

              {/*SP Advice*/}
              {px?.splAdvice?.length > 0 && (
                <View style={{ width: "100%", marginTop: "5px" }} wrap={true}>
                  <Text>
                    <Text style={styles.labelText}>
                      {getTranslatedPrescriptionTitleText(
                        "Special Advice",
                        lang
                      )}
                      :{" "}
                    </Text>
                    <Text style={{ fontFamily: "Lato", fontSize: "10px" }}>
                      {px?.splAdvice}
                    </Text>
                  </Text>
                </View>
              )}
              {/*SP Instructions*/}

              {px?.splInstructions?.length > 0 && (
                <View style={{ width: "100%", marginTop: "5px" }} wrap={false}>
                  <Text style={styles.labelText}>
                    {getTranslatedPrescriptionTitleText(
                      "Special Instructions",
                      lang
                    )}
                    :{" "}
                  </Text>
                  <View style={{ width: "100%" }}>
                    {px?.splInstructions?.map((instructionText, index) => (
                      <>
                        <View
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "start",
                          }}
                          key={index}
                        >
                          <Text
                            style={{ fontFamily: "Lato", fontSize: "10px" }}
                          >
                            {instructionText}
                          </Text>
                        </View>
                        {/* <Text style={styles.smallText} key={index}>
                      <Text> &bull;</Text>
                      {instructionText}
                    </Text> */}
                      </>
                    ))}
                  </View>
                </View>
              )}
            </>
          )}

          {/*SIGNATURE*/}
          {/* This is an Override for a special ask where Dr Akerker and Aarti name need to be shown togather in crossover clinics */}
          {isAkerkerAartiCrossoverClinic(
            px?.doctorDetails?.doctorId || "DD000001",
            clinicDetails?.clinicId || "CC000001"
          ) ? (
            <View
              style={{
                marginTop: clinicDetails?.prescriptionConfig?.signatureSpace
                  ? "40px"
                  : "20px",
                width: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-end",
                justifyContent: "space-evenly",
              }}
            >
              <View style={{ width: "30%" }}>
                <Text
                  style={{
                    fontFamily: isDevanagariScriptLanguage(lang)
                      ? "Devnagri"
                      : "Lato",
                    fontSize: "10px",
                    marginBottom: "1px",
                    textAlign: "center",
                  }}
                >
                  {" "}
                  {getTranslatedPrescribedBySentence(lang)}
                </Text>
                <Text style={styles.signatureMultiDrName}>
                  {getTranslatedPrescriptionDoctorName("DR000005", lang)}
                </Text>
                <Text
                  style={{
                    fontFamily: isDevanagariScriptLanguage(lang)
                      ? "Devnagri"
                      : "Lato",
                    fontSize: "10px",
                    marginBottom: "1px",
                    textAlign: "center",
                  }}
                >
                  {`${getTranslatedPrescriptionTitleText(
                    "Reg no.",
                    lang
                  )} - ${getDoctorsRegistrationNumber("DR000005")}`}
                </Text>
              </View>{" "}
              <View style={{ width: "30%" }}>
                <Text style={styles.signatureMultiDrName}>
                  {getTranslatedPrescriptionDoctorName("DR000001", lang)}
                </Text>
                <Text
                  style={{
                    fontFamily: isDevanagariScriptLanguage(lang)
                      ? "Devnagri"
                      : "Lato",
                    fontSize: "10px",
                    marginBottom: "1px",
                    textAlign: "center",
                  }}
                >
                  {`${getTranslatedPrescriptionTitleText(
                    "Reg no.",
                    lang
                  )} - ${getDoctorsRegistrationNumber("DR000001")}`}
                </Text>
              </View>
            </View>
          ) : clinicDetails.prescriptionConfig?.multiDoctor ? (
            <View
              style={{
                marginTop: clinicDetails.prescriptionConfig?.signatureSpace
                  ? "40px"
                  : "20px",
                width: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-end",
                justifyContent: "space-evenly",
              }}
            >
              <View style={{ width: "30%" }}>
                <Text
                  style={{
                    fontFamily: isDevanagariScriptLanguage(lang)
                      ? "Devnagri"
                      : "Lato",
                    fontSize: "10px",
                    marginBottom: "1px",
                    textAlign: "center",
                  }}
                >
                  {" "}
                  {getTranslatedPrescribedBySentence(lang)}
                </Text>
                <Text style={styles.signatureMultiDrName}>
                  {getTranslatedPrescriptionDoctorName(
                    px?.doctorDetails?.doctorId,
                    lang
                  ) || px?.doctorDetails.name}
                </Text>
              </View>{" "}
              {clinicDetails?.associatedHealthProviders
                ?.filter(
                  (healthProvider) =>
                    healthProvider.healthProviderType == "Doctor"
                )
                .map((doctor) => (
                  <View style={{ width: "30%" }}>
                    <Text style={styles.signatureMultiDrName}>
                      {getTranslatedPrescriptionDoctorName(
                        doctor?.code,
                        lang
                      ) || doctor?.healthProviderName}
                    </Text>
                  </View>
                ))}
            </View>
          ) : (
            <View
              wrap={false}
              style={{
                width: "100%",
                marginTop: clinicDetails.prescriptionConfig?.signatureSpace
                  ? "35px"
                  : "20px",
              }}
            >
              <View style={styles.signatureContainer}>
                <View style={{ marginLeft: "auto" }}>
                  <Text style={styles.smallText}>
                    {" "}
                    {getTranslatedPrescribedBySentence(lang)}
                  </Text>
                  <Text style={styles.signatureDrName}>
                    {getTranslatedPrescriptionDoctorName(
                      px?.doctorDetails?.doctorId,
                      lang
                    ) || px?.doctorDetails?.name}
                  </Text>
                </View>

                {/* {clinicDetails.prescriptionConfig?.poweredBy && (
                  <View style={styles.rightColumn}>
                    <Link
                      href="https://staging.ic4.health"
                      style={styles.linkText}
                    >
                      <Text style={{ marginRight: "10x" }}>Powered by</Text>
                      <Image
                        src="https://staging.ic4.health/patient/ic4-logo.png"
                        style={{ width: "65px" }}
                      />{" "}
                    </Link>
                  </View>
                )} */}
              </View>
            </View>
          )}
        </View>
        {!clinicDetails.prescriptionConfig?.enable && (
          <View fixed style={styles.footer}>
            <View style={{ width: "70%", fontSize: "9px" }}>
              {!(
                clinicDetails?.clinicId === "CC000005" &&
                px?.doctorDetails?.doctorId === "DR000004"
              ) && (
                <>
                  <Text>{clinicDetails.address.address + ", "}</Text>
                </>
              )}

              <Text>
                {!(
                  clinicDetails?.clinicId === "CC000005" &&
                  px?.doctorDetails.code === "DR000004"
                ) && clinicDetails.address.streetAddress + ", "}
                {clinicDetails.address.locality +
                  ", " +
                  clinicDetails.address.city +
                  ", "}
              </Text>
              <Text>
                {clinicDetails.address.state +
                  " - " +
                  clinicDetails.address.zipCode}
              </Text>
            </View>
            <View style={{ width: "30%", fontSize: "9px" }}>
              <Text>Tel.: {clinicDetails.phone}</Text>
              <Text>
                Email:{" "}
                {clinicDetails?.clinicId === "CC000005" &&
                px?.doctorDetails?.code === "DR000004"
                  ? "kshitijarthritisclinic@gmail.com"
                  : getClinicEmail(clinicDetails?.clinicId)}
              </Text>
              <Text>
                Website:{" "}
                <Link href="https://staging.ic4.health" style={styles.linkText}>
                  www.ic4.health
                </Link>
              </Text>
            </View>
            {/* <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          ></Text>

          <Link href="https://staging.ic4.health" style={styles.linkText}>
            www.ic4.health
          </Link> */}
          </View>
        )}
      </Page>
    </Document>
  );
}
